import { Injectable, signal, OnDestroy } from "@angular/core";
import { BehaviorSubject, filter, Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

type UpdateType = "market" | "runner" | "wallet" | "bet_status" | "bet_result" | "bet_placed" | "wl_betslip" | "logout" | "bet-config";

type UpdateOptions = {
  field?: string;
  value?: string;
};

@Injectable({
  providedIn: "root",
})
export class LiveUpdateService implements OnDestroy {
  public _data = signal<any>(null);
  public data = new BehaviorSubject<any>(null);
  public wsConnectionStatus = new BehaviorSubject<boolean>(false);
  private destroy$ = new Subject<void>();

  constructor() {
    this.wsConnectionStatus.pipe(takeUntil(this.destroy$)).subscribe((status) => {
      if (!status) this._setData(null);
    });
  }

  _setData(value: any): void {
    this._data.set(value);
  }

  _getData(type: UpdateType | UpdateType[], options?: UpdateOptions): any {
    const data = this._data();

    const types = Array.isArray(type) ? type : [type];

    const filterConditions: Record<string, boolean> = {
      market: Boolean(options?.field) && Boolean(options?.value) && data?.[options?.field as string] === options?.value,
      wallet: data?.type === "wallet",
      bet_status: data?.type === "bet_status",
      bet_result: data?.type === "bet_result" && Boolean(options?.value) && data?.data?.bf_bet_id === options?.value,
      bet_placed: data?.type === "bet_placed",
      wl_betslip: data?.type === "wl_betslip",
      logout: data?.type === "logout",
      "bet-config": data?.type === "bet-config",
    };

    // Check if any of the provided types match the filter conditions
    for (const updateType of types) {
      if (filterConditions[updateType]) {
        return data;
      }
    }
  }

  setData(value: any) {
    this.data.next(value);
  }

  getData(type?: UpdateType | UpdateType[], options?: UpdateOptions): Observable<any> {
    // If no type is provided, return all updates as-is
    if (!type) {
      return this.data.asObservable();
    }

    const types = Array.isArray(type) ? type : [type];

    return this.data.asObservable().pipe(
      filter((data) => {
        if (!data) return false;

        const match: Record<string, boolean> = {
          market: Boolean(options?.field) && Boolean(options?.value) && data?.[options?.field as string] === options?.value,
          wallet: data?.type === "wallet",
          bet_status: data?.type === "bet_status",
          bet_result: data?.type === "bet_result" && Boolean(options?.value) && data?.data?.bf_bet_id === options?.value,
          bet_placed: data?.type === "bet_placed",
          wl_betslip: data?.type === "wl_betslip",
          logout: data?.type === "logout",
        };

        return types.some((t) => match[t]);
      }),
    );
  }

  setConnectionStatus(status: boolean) {
    this.wsConnectionStatus.next(status);
  }

  getConnectionStatus(): Observable<boolean> {
    return this.wsConnectionStatus.asObservable();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();

    // Clear BehaviorSubjects to free memory
    this.data.next(null);
    this.wsConnectionStatus.next(false);
  }
}
